import React, { useState } from 'react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import './CustomerAuthModal.css';

const CustomerAuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState('login'); // login, signup, forgot-password
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { signIn, signUp, resetPassword } = useCustomerAuth();

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (mode === 'signup') {
      if (!formData.name.trim()) {
        setError('Name is required');
        return false;
      }
      if (!formData.phone.trim()) {
        setError('Phone number is required');
        return false;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
        setError('Please enter a valid 10-digit phone number');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (mode !== 'forgot-password' && !formData.password) {
      setError('Password is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (mode === 'login') {
        const result = await signIn(formData.email, formData.password);
        if (result.success) {
          onSuccess && onSuccess(result.user);
          onClose();
        } else {
          setError(result.error);
        }
      } else if (mode === 'signup') {
        const result = await signUp(
          formData.email,
          formData.password,
          formData.name,
          formData.phone
        );
        if (result.success) {
          onSuccess && onSuccess(result.user);
          onClose();
        } else {
          setError(result.error);
        }
      } else if (mode === 'forgot-password') {
        const result = await resetPassword(formData.email);
        if (result.success) {
          setSuccessMessage('Password reset email sent! Check your inbox.');
          setTimeout(() => {
            setMode('login');
            setSuccessMessage('');
          }, 3000);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMessage('');
    setFormData({
      name: '',
      email: formData.email,
      phone: '',
      password: '',
      confirmPassword: ''
    });
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          &times;
        </button>

        <div className="auth-modal-header">
          <h2>
            {mode === 'login' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'forgot-password' && 'Reset Password'}
          </h2>
          <p>
            {mode === 'login' && 'Sign in to track orders and manage favorites'}
            {mode === 'signup' && 'Join us for a better ordering experience'}
            {mode === 'forgot-password' && 'Enter your email to reset password'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <>
              <div className="auth-form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit phone number"
                />
              </div>
            </>
          )}

          <div className="auth-form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
            />
          </div>

          {mode !== 'forgot-password' && (
            <div className="auth-form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
              />
            </div>
          )}

          {mode === 'signup' && (
            <div className="auth-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
              />
            </div>
          )}

          {error && <div className="auth-error">{error}</div>}
          {successMessage && <div className="auth-success">{successMessage}</div>}

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? 'Please wait...' : (
              mode === 'login' ? 'Sign In' :
              mode === 'signup' ? 'Create Account' :
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="auth-modal-footer">
          {mode === 'login' && (
            <>
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => switchMode('forgot-password')}
              >
                Forgot password?
              </button>
              <p>
                Don't have an account?{' '}
                <button
                  type="button"
                  className="auth-link-btn primary"
                  onClick={() => switchMode('signup')}
                >
                  Sign Up
                </button>
              </p>
            </>
          )}

          {mode === 'signup' && (
            <p>
              Already have an account?{' '}
              <button
                type="button"
                className="auth-link-btn primary"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>
          )}

          {mode === 'forgot-password' && (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                className="auth-link-btn primary"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerAuthModal;
